import { SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import React, { useState } from 'react';
import { Resizable, ResizeHandle } from 'react-resizable';

type ResizableBoxProps = {
  children: React.ReactNode;
  initialWidth: number;
  handlePosition: 'left' | 'right';
  handleWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onResize?: (newWidth: number) => void;
};

/**
 * `ResizableWrapper` component allows its children to be resizable horizontally.
 *
 * @component
 * @param {ResizableBoxProps} props - The properties of the component.
 * @returns {React.FC} The resizable wrapper component.
 *
 * @example
 * ```tsx
 * <ResizableWrapper initialWidth={300} handlePosition="right">
 *   <YourComponent />
 * </ResizableWrapper>
 * ```
 */
const ResizableWrapper: React.FC<ResizableBoxProps> = ({
  children,
  initialWidth,
  handlePosition,
  handleWidth = 20,
  minWidth = 10,
  maxWidth = document.body.clientWidth,
  onResize
}) => {
  const [width, setWidth] = useState(initialWidth);

  const handleXPos = -(handleWidth / 2); //Make handle hover centrally over its border

  const handleOptions: { position: ResizeHandle; sx: SxProps } = {
    position: handlePosition === 'left' ? 'w' : 'e',
    sx: {
      ...(handlePosition === 'left' ? { left: handleXPos } : { right: handleXPos })
    }
  };

  return (
    <Resizable
      width={width}
      height={100}
      axis='x'
      maxConstraints={[maxWidth, 0]}
      minConstraints={[minWidth, 0]}
      onResize={(event, { size }) => {
        setWidth(size.width);
      }}
      onResizeStop={(e, data) => onResize && onResize(data.size.width)}
      resizeHandles={[handleOptions.position]}
      handle={(_, ref) => (
        <Box
          ref={ref}
          sx={{
            position: 'absolute',
            height: '100%',
            zIndex: 100,
            cursor: 'ew-resize',
            width: `${handleWidth}px`,
            top: 0,
            ...handleOptions.sx
          }}
        />
      )}
    >
      <Box
        sx={{
          position: 'relative',
          width,
          height: '100%',
          backgroundColor: 'transparent',
          flexGrow: 0,
          flexShrink: 0
        }}
      >
        {children}
      </Box>
    </Resizable>
  );
};

export default ResizableWrapper;
